import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from 'linaria';
import cn from 'clsx';
import {
  ButtonWithGAClickButton,
  ModalMediaCardBody,
  ModalMediaCardFooter,
  ModalMediaCardGallery,
  ModalMediaCardImage,
  MoreInfoCard,
  MoreInfoCardBody,
  MoreInfoCardGrid,
  MoreInfoCardImage,
  SecondarySection,
  SectionSubTitle,
  Typography,
} from 'common/components';
import { usePromoViewEvent } from 'common/hooks';
import { createElementKey } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import { WINDOWS_ELEMENT_BLOCK } from '../../windows-constants';
import { WINDOWS_TYPE_CARD_LIST, WindowsTypeCardStructure } from './windows-type-section-constants';

type WindowsTypesSectionProps = {
  onConsultationSignupBtnClick: () => void;
};

const windowsMoreInfoCardBody = css`
  @media (--only-desktop) {
    p {
      width: 200px;
    }
  }
`;

export const WindowsTypeSection = ({ onConsultationSignupBtnClick }: WindowsTypesSectionProps) => {
  const { t } = useTranslation();
  const cardRefs = useRef<HTMLDivElement[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [openCardIndex, setOpenCardIndex] = useState<number | undefined>(undefined);

  const promotions = useMemo(
    () =>
      WINDOWS_TYPE_CARD_LIST.map(({ title }: WindowsTypeCardStructure, index: number) => ({
        name: WINDOWS_ELEMENT_BLOCK,
        creative: title,
        position: (index + 1).toString(),
      })),
    []
  );

  usePromoViewEvent(cardRefs, WINDOWS_ELEMENT_BLOCK, promotions);

  /**
   * Обработчик нажатия на карточку со стилями
   */
  const handleCardClick = useCallback(
    (index: number) => () => {
      setOpenCardIndex(index);
      setIsModalOpen(true);
      analyticsUtils.promoClickEvent(WINDOWS_ELEMENT_BLOCK, promotions[index]);
    },
    [promotions, setIsModalOpen, setOpenCardIndex]
  );

  const handleFindOutPriceBtnClick = useCallback(() => {
    setIsModalOpen(false);
    setOpenCardIndex(0);
    onConsultationSignupBtnClick();
  }, [onConsultationSignupBtnClick]);

  /**
   * Обработчик закрытия модального окна
   */
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const handleCardChange = useCallback((index: number) => {
    setOpenCardIndex(index);
  }, []);

  return (
    <SecondarySection>
      <SectionSubTitle margin="m">{t('type-section.title')}</SectionSubTitle>
      <MoreInfoCardGrid cardPerRow={3}>
        {WINDOWS_TYPE_CARD_LIST.map(({ imgSrc, title }: WindowsTypeCardStructure, index: number) => (
          <MoreInfoCard
            data-position={index}
            key={createElementKey('windows-type', index)}
            onClick={handleCardClick(index)}
            ref={(el: HTMLDivElement | null) => {
              if (el && cardRefs) cardRefs.current[index] = el;
            }}>
            <MoreInfoCardImage alt={t(title) || ''} loading="lazy" src={imgSrc} />
            <MoreInfoCardBody className={cn({ [windowsMoreInfoCardBody]: index === 3 })} title={t(title)} />
          </MoreInfoCard>
        ))}
      </MoreInfoCardGrid>

      <ModalMediaCardGallery
        cardList={WINDOWS_TYPE_CARD_LIST}
        elementBlock={WINDOWS_ELEMENT_BLOCK}
        isOpen={isModalOpen}
        openCardIndex={openCardIndex}
        onCardChange={handleCardChange}
        onClose={handleCloseModal}
        renderContent={({ description, modalImgSrc, title }) => (
          <>
            <ModalMediaCardBody title={t(title) || ''}>
              <Typography adaptive={false} variant="m" size="body_long">
                {t(description)}
              </Typography>

              <ModalMediaCardFooter>
                <ButtonWithGAClickButton
                  elementBlock={WINDOWS_ELEMENT_BLOCK}
                  elementName={t('type-section.button')}
                  elementText={t(title)}
                  onClick={handleFindOutPriceBtnClick}
                  wide={false}>
                  {t('type-section.button')}
                </ButtonWithGAClickButton>
              </ModalMediaCardFooter>
            </ModalMediaCardBody>
            <ModalMediaCardImage alt={title} src={modalImgSrc} title={title} />
          </>
        )}
      />
    </SecondarySection>
  );
};
